import React from 'react';
import {
    ArrowRight,
    Link as LinkIcon,
    Layers,
    Mail,
} from 'lucide-react';

/* ------------------------------------------------------------------
 * Types
 * ---------------------------------------------------------------- */

export type RelatedLinkIcon =
    | 'arrow'
    | 'link'
    | 'layers'
    | 'mail';

export type RelatedLinkItem = {
    label: string;
    href: string;
    icon?: RelatedLinkIcon;
};

export type RelatedLinksProps = {
    title?: string;
    items: RelatedLinkItem[];
    dark?: boolean;
};

/* ------------------------------------------------------------------
 * Icon whitelist
 * ---------------------------------------------------------------- */

const ICON_MAP: Record<RelatedLinkIcon, React.ElementType> = {
    arrow: ArrowRight,
    link: LinkIcon,
    layers: Layers,
    mail: Mail,
};

/* ------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------- */

const RelatedLinks = ({ title, items, dark }: RelatedLinksProps) => {
    if (!items?.length) return null;

    const textClass = dark ? 'text-white' : 'text-gray-900';
    const subTextClass = dark ? 'text-gray-300' : 'text-gray-700';
    const hoverClass = dark
        ? 'hover:text-white'
        : 'hover:text-gray-900';

    return (
        <aside
            className={`rounded-lg p-4 ${
                dark
                    ? 'bg-gray-900'
                    : 'bg-gray-50'
            }`}
        >
            {!!title && (
                <h6
                    className={`mb-3 text-sm font-semibold uppercase tracking-wide ${textClass}`}
                >
                    {title}
                </h6>
            )}

            <ul className="space-y-2">
                {items.map(({ label, href, icon }) => {
                    const Icon = icon ? ICON_MAP[icon] : null;

                    return (
                        <li key={label}>
                            <a
                                href={href}
                                className={`flex items-start gap-2 text-sm ${subTextClass} ${hoverClass}`}
                            >
                                {Icon ? (
                                    <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 opacity-80" />
                                ) : (
                                    <span className="mt-2 h-1 w-1 rounded-full bg-current opacity-60" />
                                )}

                                <span className="leading-snug">
                                    {label}
                                </span>
                            </a>
                        </li>
                    );
                })}
            </ul>
        </aside>
    );
};

export default RelatedLinks;
